import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

export type UnreadIncident = components["schemas"]["UnreadIncident"];

export const incidentReadKeys = {
  unread: ["incidents", "unread-mine"] as const,
};

/**
 * Phiếu lỗi đã xác nhận mà CHÍNH người đang đăng nhập chưa đọc (Sổ vi phạm,
 * CEO chốt 3a + 5a). Cửa không nhận mã nhân sự — máy chủ lấy từ phiên.
 */
export function useUnreadIncidents(enabled: boolean) {
  return useQuery({
    queryKey: incidentReadKeys.unread,
    enabled,
    // Phiếu mới được xác nhận trong lúc app đang mở cũng phải hiện ra.
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/incidents/unread-mine", {});
      if (error) throw new Error(serverErrorMessage(error, "Không tải được phiếu lỗi cần đọc"));
      return data?.incidents ?? [];
    },
  });
}

export function useMarkIncidentRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; explanation: string }) => {
      const { data, error } = await api.POST("/incidents/{id}/read", {
        params: { path: { id: input.id } },
        body: { explanation: input.explanation },
      });
      if (error) throw new Error(serverErrorMessage(error, "Chưa ghi nhận được — thử lại"));
      return data;
    },
    // Gỡ phiếu vừa đọc khỏi danh sách NGAY (không chờ máy chủ trả lại), rồi CHỜ làm
    // tươi xong mới coi là hết mutation. Bắn rồi bỏ thì lúc nút hết xoay vẫn còn một
    // nhịp màn chặn đang hiện đúng phiếu cũ, bấm lần nữa là đọc trùng.
    onSuccess: async (_data, input) => {
      qc.setQueryData<UnreadIncident[]>(incidentReadKeys.unread, (old) =>
        old ? old.filter((x) => x.id !== input.id) : old,
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: incidentReadKeys.unread }),
        qc.invalidateQueries({ queryKey: ["performance"] }),
      ]);
    },
  });
}
